import { useForm } from 'react-hook-form';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { HubSpotEvents } from '@/shared/thirdParty/hubSpot';
import { API } from '@/shared/api';
import { showErrorAlert } from '@/shared/utils-tmp/alert/showErrorAlert';
import { useAppSelector } from '@/shared/hooks-tmp/reduxToolkitHooks';
import { OPTIONS, TTS_OPTIONS } from '../constants';
import { FormValues } from '../types';
import { getCheckboxLabels } from '../utils/getCheckboxLabels';
import { getHubSpotEventKey } from '../utils/getHubSpotEventKey';

type TUseTranslatedVideoFeedbackForm = {
  rating?: number;
  resetRating?: () => void;
  onSubmitFeedback?: () => void;
  onClose: () => void;
};

export const useTranslatedVideoFeedbackForm = ({
  rating,
  onSubmitFeedback,
  resetRating,
  onClose,
}: TUseTranslatedVideoFeedbackForm) => {
  const user = useAppSelector((state) => state.user);
  const { project } = useAppSelector((state) => state.project);

  const isRaskTtsUsed = project?.used_rask_tts;

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      options: Array.from(Array(isRaskTtsUsed ? TTS_OPTIONS.length : OPTIONS.length), () => false),
      text: '',
    },
  });

  const { options, text } = watch();
  const isFormValid = options.includes(true) || text.trim().length > 0;

  const onSubmit = async (formValues: FormValues) => {
    try {
      if (!project?.id || !rating || !user) {
        return;
      }

      const checkboxLabels = getCheckboxLabels(formValues.options, isRaskTtsUsed);

      const hubSpotEventKey = getHubSpotEventKey(formValues.options);

      const response = await API.postFeedback({
        feedback_type: isRaskTtsUsed ? 'rask_tts_translation' : 'translation',
        id: project?.id,
        rating_score: rating,
        ...(formValues.text && { text: formValues.text }),
        ...(checkboxLabels.length && { options: checkboxLabels }),
      });

      if (response.ok) {
        MixpanelEvents.rateResultSurvey({ rating });
        if (!isRaskTtsUsed) {
          HubSpotEvents.sendVideoFeedback({ event: hubSpotEventKey, user_id: user.id });
        }

        onSubmitFeedback?.();
        onClose();
      }
    } catch (e) {
      showErrorAlert(e);
    }
  };

  const onCancel = () => {
    if (isSubmitting) {
      return;
    }

    resetRating?.();
    onClose();
  };

  return { isLoading: isSubmitting, isFormValid, control, onCancel, onSubmit: handleSubmit(onSubmit) };
};
